import { SxProps } from "@mui/material";
import { dt } from "../../themes/redesignTokens";
import { GalleryVariant } from "./ImageGalleryTypes";

// --- Variant sizes (responsive) ---

interface ResponsiveSize {
  width: string;
  height: string;
}

interface VariantSizeConfig {
  desktop: ResponsiveSize;
  tablet: ResponsiveSize;
  phone: ResponsiveSize;
}

export const variantSizes: Record<GalleryVariant, VariantSizeConfig> = {
  card: {
    desktop: { width: "128px", height: "128px" },
    tablet: { width: "100px", height: "100px" },
    phone: { width: "80px", height: "80px" },
  },
  profile: {
    desktop: { width: "200px", height: "200px" },
    tablet: { width: "180px", height: "180px" },
    phone: { width: "160px", height: "160px" },
  },
  detail: {
    desktop: { width: "480px", height: "300px" },
    tablet: { width: "100%", height: "240px" },
    phone: { width: "100%", height: "200px" },
  },
};

// --- Swiper container sx per variant ---

export const getGallerySx = (
  variant: GalleryVariant,
  tier: "desktop" | "tablet" | "phone"
): SxProps => {
  const size = variantSizes[variant][tier];

  return {
    width: size.width,
    height: size.height,
    position: "relative",
    overflow: "hidden",
    borderRadius: dt.radiusSm,
    backgroundColor: dt.cardBg,
    border: `1px solid ${dt.cardBorder}`,
    boxShadow: dt.cardShadow,
    fontFamily: dt.font,
    // Swiper overrides
    "& .swiper": {
      width: "100%",
      height: "100%",
    },
    "& .swiper-slide": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
    "& .swiper-slide img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: dt.radiusSm,
      userSelect: "none",
      WebkitUserDrag: "none" as any,
    },
    // Navigation arrow styling
    "& .swiper-button-prev, & .swiper-button-next": {
      color: "#FFFFFF",
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      backgroundColor: "rgba(0,0,0,0.35)",
      transition: "all 0.2s ease",
      opacity: 0,
      "&::after": {
        fontSize: "12px",
        fontWeight: 700,
      },
      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.55)",
      },
    },
    // Show arrows on hover (desktop) or always (touch)
    "&:hover .swiper-button-prev, &:hover .swiper-button-next": {
      opacity: 1,
    },
    "@media (hover: none)": {
      "& .swiper-button-prev, & .swiper-button-next": {
        opacity: 1,
      },
    },
    // Hide disabled arrows
    "& .swiper-button-disabled": {
      opacity: "0 !important",
    },
    // Pagination dots
    "& .swiper-pagination-bullet": {
      width: "6px",
      height: "6px",
      backgroundColor: "#FFFFFF",
      opacity: 0.5,
      transition: "all 0.2s ease",
    },
    "& .swiper-pagination-bullet-active": {
      opacity: 1,
      backgroundColor: dt.blue,
      width: "8px",
      height: "8px",
    },
  };
};

// --- Counter overlay ---

export const counterSx: SxProps = {
  position: "absolute",
  bottom: "8px",
  right: "8px",
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  gap: "3px",
  backgroundColor: "rgba(0,0,0,0.55)",
  color: "#FFFFFF",
  borderRadius: "12px",
  padding: "2px 8px",
  fontSize: "11px",
  fontWeight: 600,
  fontFamily: dt.fontMono,
  letterSpacing: "0.3px",
  pointerEvents: "none",
  userSelect: "none",
};

// --- Fallback placeholder ---

export const fallbackSx: SxProps = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: dt.inputBg,
  borderRadius: dt.radiusSm,
  color: dt.textMuted,
  gap: 0.5,
  "& img": {
    width: "40%",
    height: "40%",
    objectFit: "contain",
    opacity: 0.4,
  },
};

// --- Lightbox overlay ---

export const lightboxSx: SxProps = {
  // Semi-transparent backdrop — app visible behind
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0,0,0,0.65)",
  },
  // Centered modal paper — ~70vw x ~75vh, dark bg, rounded corners
  "& .MuiDialog-paper": {
    backgroundColor: "rgba(15,15,20,0.95)",
    borderRadius: "16px",
    boxShadow: "0 24px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)",
    margin: "auto",
    maxWidth: "70vw",
    maxHeight: "80vh",
    width: "70vw",
    height: "75vh",
    position: "relative",
    overflow: "hidden",
  },
  // Responsive: on phone, use nearly full screen
  "@media (max-width: 768px)": {
    "& .MuiDialog-paper": {
      maxWidth: "95vw",
      maxHeight: "90vh",
      width: "95vw",
      height: "85vh",
      borderRadius: "12px",
      margin: "16px",
    },
  },
  // Swiper fills the dialog
  "& .swiper": {
    width: "100%",
    height: "100%",
  },
  "& .swiper-slide": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .swiper-slide img": {
    maxWidth: "95%",
    maxHeight: "calc(100% - 80px)", // leave room for toolbar + counter
    objectFit: "contain",
    userSelect: "none",
    WebkitUserDrag: "none" as any,
  },
  // Navigation arrows
  "& .swiper-button-prev, & .swiper-button-next": {
    color: "#FFFFFF",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.12)",
    transition: "all 0.2s ease",
    "&::after": {
      fontSize: "14px",
      fontWeight: 700,
    },
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.25)",
    },
  },
  "& .swiper-button-disabled": {
    opacity: "0.3 !important",
  },
};

// --- Lightbox toolbar ---

export const lightboxToolbarSx: SxProps = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "10px 14px",
  gap: "6px",
};

// --- Lightbox counter (bottom center) ---

export const lightboxCounterSx: SxProps = {
  position: "absolute",
  bottom: "14px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 20,
  color: "#FFFFFF",
  fontSize: "13px",
  fontWeight: 600,
  fontFamily: dt.fontMono,
  letterSpacing: "0.5px",
  pointerEvents: "none",
  userSelect: "none",
  opacity: 0.8,
};

// --- Thumbnail strip ---

export const thumbnailStripSx: SxProps = {
  display: "flex",
  gap: "6px",
  padding: "6px 0",
  "& .swiper": {
    width: "100%",
  },
  "& .swiper-slide": {
    width: "56px !important",
    height: "56px",
    flexShrink: 0,
    cursor: "pointer",
  },
};

export const getThumbnailItemSx = (isActive: boolean): SxProps => ({
  width: "56px",
  height: "56px",
  borderRadius: "4px",
  overflow: "hidden",
  border: isActive
    ? `2px solid ${dt.blue}`
    : `1.5px solid ${dt.cardBorder}`,
  opacity: isActive ? 1 : 0.7,
  transition: "all 0.2s ease",
  cursor: "pointer",
  flexShrink: 0,
  "&:hover": {
    opacity: 1,
    borderColor: isActive ? dt.blue : dt.textMuted,
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
});
